import { usePlaybackState, usePlaybackStore } from '@app/core/playback/hooks/usePlayback';
import { playbackRegistry } from '@app/core/playback/registry/playback-registry';
import { useVideoPlaybackState } from '@app/core/playback/video/useVideoPlaybackState';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { IconButton, Tooltip } from '@mui/material';
import { useCallback } from 'react';

/**
 * Volume toggle control for the live player.
 * Uses the video playback store to control the video.js player's mute state.
 */
export function LivePlayerMuteControl({ size = 'small' }: { size?: 'small' | 'medium' }) {
  const store = usePlaybackStore();
  const videoJsPlayer = useVideoPlaybackState((s) => s.videoJsPlayer);
  const isMuted = usePlaybackState((s) => s.isMuted);
  const mute = usePlaybackState((s) => s.mute);
  const unmute = usePlaybackState((s) => s.unmute);

  const handleToggleMute = useCallback(() => {
    if (!videoJsPlayer) {
      return;
    }

    // Toggle mute state using playback store methods
    if (isMuted) {
      unmute();

      // when unmuting, apply exclusive playback mode to all other players
      // (other players will be paused or be muted depending on their exclusive playback mode)
      playbackRegistry.applyExclusivePlaybackMode(store);
    } else {
      mute();
    }
  }, [videoJsPlayer, isMuted, mute, unmute]);

  // Don't render if player is not available
  if (!videoJsPlayer) {
    return null;
  }

  return (
    <Tooltip title={isMuted ? 'Activar sonido' : 'Silenciar'}>
      <IconButton size={size} onClick={handleToggleMute}>
        {isMuted ? (
          <VolumeOffIcon fontSize={size === 'small' ? 'small' : undefined} />
        ) : (
          <VolumeUpIcon fontSize={size === 'small' ? 'small' : undefined} />
        )}
      </IconButton>
    </Tooltip>
  );
}
